import { useLayoutEffect, useRef } from 'react';
import { SRGBColorSpace, type Mesh, type Texture } from 'three';
import type { SurfacePlacement } from '../types';

export interface SurfaceDecalProps {
  /**
   * 배치만 받습니다(SurfaceDecalConfig의 id·image는 쓰지 않습니다).
   *
   * 이렇게 좁혀 둔 덕에 번호판 판(PlatePanelConfig)도 같은 컴포넌트로 그립니다.
   * 텍스처를 어디서 얻는지만 다르고(데칼은 이미지 파일, 번호판은 구운 캔버스)
   * "실측 평면에 판을 세운다"는 일은 같습니다.
   */
  decal: SurfacePlacement;
  texture: Texture | null | undefined;
}

/**
 * 차량 표면에 붙이는 이미지 데칼(엠블럼 등).
 *
 * 왜 메시의 UV에 텍스처를 입히지 않고 판을 덧붙이는가:
 *   붙일 부품의 UV는 그 부품용으로 잡혀 있지 않은 경우가 많습니다. Taycan의
 *   스티어링 센터 패드는 실내 전체와 머티리얼을 공유하고 UV도 그에 맞춰져 있어서,
 *   크레스트를 UV로 얹으면 엉뚱한 곳에 늘어납니다. 작은 판을 표면에 맞춰
 *   덧붙이면 위치·크기·방향을 정확히 통제할 수 있습니다.
 *
 * 좌표는 앱 좌표계(차량 rotationY·scale 적용 후)이므로 차량과 형제로 둡니다.
 */
export function SurfaceDecal({ decal, texture }: SurfaceDecalProps) {
  const meshRef = useRef<Mesh | null>(null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (mesh === null) {
      return;
    }
    /*
     * 이미지의 위쪽이 향할 방향. lookAt은 판이 어느 쪽을 보는지만 정하고,
     * 그 축을 중심으로 얼마나 돌아갈지(롤)는 mesh.up으로 정해집니다.
     *
     * 기본값 (0,1,0)은 세워진 면에서는 잘 동작하지만, 보닛처럼 법선이 거의
     * 수직인 면에서는 바라보는 방향과 up이 평행해져 회전이 무너집니다
     * (cross(up, z)가 0이 되어 방향을 못 정함). 그런 면에는 up을 따로 줘야 합니다.
     *
     * 보닛 크레스트는 up이 차량 뒤쪽(-X)입니다. 차 앞에 서서 보닛을 내려다보면
     * 멀리 있는 쪽(윈드실드 방향)이 시야의 위가 되기 때문입니다.
     */
    if (decal.up !== undefined) {
      mesh.up.set(decal.up[0], decal.up[1], decal.up[2]);
    }
    /*
     * 붙일 면의 실측 법선 방향을 바라보게 합니다. 오일러 각을 직접 계산하면
     * 회전 순서 때문에 부호를 틀리기 쉽고, 법선을 조금 고칠 때마다 다시 계산해야
     * 합니다. lookAt은 "이 방향을 봐라"를 그대로 표현합니다.
     */
    mesh.lookAt(
      decal.position[0] + decal.normal[0],
      decal.position[1] + decal.normal[1],
      decal.position[2] + decal.normal[2]
    );
  }, [decal]);

  useLayoutEffect(() => {
    if (texture === null || texture === undefined) {
      return;
    }
    /*
     * TextureLoader는 색 공간을 지정하지 않습니다. 색 텍스처에 sRGB를 지정하지
     * 않으면 감마 보정이 두 번 적용되지 않아 색이 탁하게 나옵니다.
     */
    if (texture.colorSpace !== SRGBColorSpace) {
      texture.colorSpace = SRGBColorSpace;
      texture.needsUpdate = true;
    }
  }, [texture]);

  if (texture === null || texture === undefined) {
    return null;
  }

  return (
    <mesh ref={meshRef} position={decal.position}>
      <planeGeometry args={decal.size} />
      {/*
        transparent 대신 alphaTest를 씁니다. 반투명으로 처리하면 그리는 순서에
        따라 데칼이 차체 뒤로 사라지는 일이 생깁니다. 배지는 반투명 영역이 없고
        "있음/없음"만 있으므로 알파 컷으로 충분합니다.
      */}
      {/*
        envMapIntensity를 데칼별로 받습니다. 실내 배지는 빛이 약해서 세게 줘야
        금속감이 살지만, 외장 배지는 스튜디오 조명을 정면으로 받으면 같은 값에서
        하이라이트가 터져 크레스트가 흰 덩어리로 뭉개집니다.
      */}
      <meshStandardMaterial
        map={texture}
        alphaTest={0.4}
        metalness={decal.metalness ?? 0.35}
        roughness={decal.roughness ?? 0.35}
        envMapIntensity={decal.envMapIntensity ?? 1.4}
      />
    </mesh>
  );
}
